import { useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

/** `2026-03` → `mar/26`. Eixo com o mês por extenso não cabe em doze colunas. */
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export function rotuloMes(month: string): string {
  const [ano, mes] = month.split('-');
  const indice = Number(mes) - 1;
  return `${MESES[indice] ?? mes}/${(ano ?? '').slice(2)}`;
}

/**
 * Teto "redondo" para as linhas de referência.
 *
 * Com o maior valor no topo, a linha de cima dizia `R$ 1,37 mi` e a do meio
 * `R$ 685 mil` — número que ninguém compara de cabeça. O teto sobe para o
 * próximo múltiplo aceito de uma potência de dez — 2,9 mi vira 3 mi, não 5 mi,
 * senão a coluna mais alta fica na metade do cartão. Em contagem inteira o
 * teto é par, para a linha do meio também ser inteira.
 */
/** Múltiplos aceitos para o teto, dentro de cada potência de dez. */
const PASSOS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

export function tetoRedondo(max: number, inteiro = false): number {
  if (max <= 0) return inteiro ? 4 : 1;

  if (inteiro) return max <= 4 ? 4 : Math.ceil(max / 2) * 2;

  const expoente = 10 ** Math.floor(Math.log10(max));
  const fracao = max / expoente;
  const base = PASSOS.find((passo) => fracao <= passo) ?? 10;

  return base * expoente;
}

export interface TipRow {
  readonly label: string;
  readonly value: string;
}

/**
 * Dica sobre a marca: superfície invertida, sem seta.
 *
 * O valor vem em branco e o rótulo um tom abaixo — aqui a pessoa já sabe a
 * série e quer o número. Nas pontas do gráfico a dica se alinha ao lado de
 * dentro, para não sair do cartão.
 */
export function ChartTip({
  title,
  rows,
  align = 'center',
}: {
  title: string;
  rows: readonly TipRow[];
  align?: 'start' | 'center' | 'end' | undefined;
}) {
  return (
    <span
      role="tooltip"
      className={cn(
        'on-dark pointer-events-none absolute bottom-full z-10 mb-2 rounded-sm bg-foreground px-2.5 py-1.5 text-[12px] whitespace-nowrap text-white',
        align === 'center' && 'left-1/2 -translate-x-1/2',
        align === 'start' && 'left-0',
        align === 'end' && 'right-0',
      )}
    >
      <span className="block font-medium">{title}</span>
      {rows.map((row) => (
        <span key={row.label} className="tabular flex justify-between gap-4 text-white/70">
          <span>{row.label}</span>
          <span className="text-white">{row.value}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Moldura de gráfico: a leitura-resumo à esquerda, "Ver tabela" à direita, e a
 * tabela no lugar do desenho quando pedida. Todo gráfico tem a tabela gêmea —
 * é o que torna o número alcançável sem ponteiro e sem cor.
 */
export function ChartFrame({
  summary,
  table,
  children,
}: {
  summary?: ReactNode;
  table: ReactNode;
  children: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);

  return (
    <div className="px-5 pb-5">
      <div className="mb-3 flex items-center justify-between gap-3 text-[13px] text-muted-foreground">
        <div className="tabular min-w-0">{summary}</div>
        {/* No papel o botão não leva a lugar nenhum. */}
        <button
          type="button"
          onClick={() => setAsTable((value) => !value)}
          className="shrink-0 transition-colors hover:text-foreground print:hidden"
        >
          {asTable ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>

      {asTable ? table : children}
    </div>
  );
}

/** A tabela gêmea: primeira coluna à esquerda, numéricas à direita. */
export function ChartTable({
  head,
  rows,
}: {
  head: readonly string[];
  rows: readonly (readonly string[])[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="tabular w-full text-[13px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            {head.map((label, index) => (
              <th key={label} className={cn('py-1.5 font-medium', index > 0 && 'text-right')}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, index) => (
                <td
                  key={index}
                  className={cn(
                    'py-1.5',
                    index > 0 && 'text-right',
                    index > 1 && 'text-muted-foreground',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
